import React from 'react';
import {
  TouchableOpacity,
  Text,
  StyleSheet,
  Platform,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { colors, fontSize, fontWeight, spacing } from '../../theme';

export type FormCompactPrimaryButtonProps = {
  onPress: () => void;
  /** Full VoiceOver / TalkBack description (action + target). */
  accessibilityLabel: string;
  disabled?: boolean;
  testID?: string;
  children: React.ReactNode;
  /** Optional leading icon (e.g. Link2); tint with `colors.textLight`. */
  icon?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
};

/**
 * Compact solid primary control (orange fill, light text) — same tokens as Deal registration **Link deal**.
 */
export const FormCompactPrimaryButton: React.FC<FormCompactPrimaryButtonProps> = ({
  onPress,
  accessibilityLabel,
  disabled = false,
  testID,
  children,
  icon,
  style,
}) => (
  <TouchableOpacity
    style={[styles.button, disabled && styles.buttonDisabled, style]}
    onPress={onPress}
    disabled={disabled}
    accessibilityRole="button"
    accessibilityLabel={accessibilityLabel}
    activeOpacity={disabled ? 1 : 0.88}
    testID={testID}
  >
    {icon ?? null}
    <Text style={styles.text}>{children}</Text>
  </TouchableOpacity>
);

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    flexShrink: 0,
    gap: spacing.sm,
    backgroundColor: colors.primary,
    paddingVertical: spacing.formCompactActionPaddingVertical,
    paddingHorizontal: spacing.formCompactActionPaddingHorizontal,
    borderRadius: spacing.cardBorderRadius,
    ...Platform.select({
      web: {
        /**
         * RN Web often maps `TouchableOpacity` to `<button>`; UA border (~`borderMedium`) competes
         * with the parent card stroke — same pattern as {@link FormDropdown} `webTriggerRow`.
         */
        borderStyle: 'solid' as const,
        borderWidth: 0,
        borderColor: 'transparent',
        border: 'none',
        outlineWidth: 0,
        outlineStyle: 'none' as const,
        boxShadow: 'none' as const,
        appearance: 'none' as const,
        WebkitAppearance: 'none' as const,
        cursor: 'pointer' as const,
      },
      default: {},
    }),
  },
  buttonDisabled: {
    opacity: 0.45,
  },
  text: {
    color: colors.textLight,
    fontWeight: fontWeight.semibold,
    fontSize: fontSize.body,
  },
});
